"use client";

import { IconCheck, IconCopy } from "@tabler/icons-react";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import { useKeyboardScrollable } from "shared-web/lib/useKeyboardScrollable";
import IconButton from "src/components/common/IconButton/IconButton";
import { successToast } from "src/components/common/Toasts/ToastActions";
import Typography from "src/components/common/Typography/Typography";
import { useExtracted } from "#i18n";
import styles from "./Snippet.module.scss";

export interface SnippetProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  showCopyButton?: boolean;
  copyDisabled?: boolean;
  /** Keep a single line without adding a scrollbar as the value changes. */
  truncate?: boolean;
  /** Inline controls stay outside the copied text. */
  actions?: React.ReactNode;
  /** Leading content stays outside the copied text. */
  icon?: React.ReactNode;
  /**
   * Both variants use the same recessed background. `inline` (the default) is as wide as its
   * text; `field` fills its row, for values that hold editable parts (a request URL).
   */
  variant?: "inline" | "field";
}

const Snippet = React.memo((props: SnippetProps) => {
  const translateMessage = useExtracted();
  const {
    children,
    className,
    icon,
    actions,
    showCopyButton = false,
    copyDisabled = false,
    truncate = false,
    variant = "inline",
    ...rest
  } = props;
  const snippetRef = useRef<HTMLDivElement>(null);
  const scrollTabIndex = useKeyboardScrollable(snippetRef);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      // biome-ignore lint/performance/useDomNodeTextContent: copy the rendered text, without visually hidden labels.
      const text = (snippetRef.current?.innerText ?? "").replace(/\n/g, "").trim();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      successToast(translateMessage("Copied to clipboard"), {
        toastId: "copy-to-clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      {...rest}
      className={clsx(styles.root, className)}
      data-variant={variant}
      data-truncate={truncate || undefined}
    >
      {icon && (
        <span className="inline-flex shrink-0 items-center pe-1" aria-hidden="true">
          {icon}
        </span>
      )}
      {/* Only the text scrolls, so the scrollbar stays inside the padding and rounded corners. */}
      <div ref={snippetRef} tabIndex={scrollTabIndex} className={styles.scroller}>
        <Typography as="code" variant="code" truncate={truncate} className={styles.content}>
          {children}
        </Typography>
      </div>
      {actions && <div className="flex shrink-0 items-center">{actions}</div>}
      {showCopyButton && (
        <IconButton
          label={copied ? translateMessage("Copied") : translateMessage("Copy")}
          onClick={copyToClipboard}
          disabled={copyDisabled}
          size="sm"
          variant="bare"
          ripple={false}
          className={styles.copyButton}
        >
          {copied ? <IconCheck size={16} className="text-success-text" /> : <IconCopy size={16} />}
        </IconButton>
      )}
    </div>
  );
});

export default Snippet;
